import { useState, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, FileText, Search, Printer, Mail, Trash2, DollarSign, Lock, Download, History, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PricingBlock from "@/components/PricingBlock";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function money(n) {
  const v = Number(n || 0);
  return `${v < 0 ? "-" : ""}$${Math.abs(v).toFixed(2)}`;
}

// House net = client total − driver pay − government remittances. Admin-only.
// Cancelled jobs return null (column displays "—"). Mirrors HistoryPage.
function computeHouseNet(job) {
  if (job.status === "cancelled") return null;
  const total = parseFloat(job.total) || 0;
  const driverTotal = parseFloat(job.driver_total) || 0;
  const tax = parseFloat(job.tax) || 0;
  const paFee = parseFloat(job.pa_fee) || 0;
  const cgFee = parseFloat(job.cg_fee) || 0;
  const mode = job.pricing_mode || "all_inclusive";
  const bcf = parseFloat(mode === "line_items" ? job.bcf_charge : job.bcf_liability) || 0;
  const sqFee = parseFloat(job.square_processing_fee) || 0;
  if (total === 0 && driverTotal === 0) return null;
  return total - driverTotal - tax - paFee - cgFee - bcf - sqFee;
}

function NetCell({ job }) {
  const n = computeHouseNet(job);
  if (n === null) return <span className="text-xs text-muted-foreground/40 tabular-nums">—</span>;
  return (
    <span
      className={`text-xs tabular-nums font-semibold ${n >= 0 ? "text-emerald-500 dark:text-emerald-400" : "text-red-500"}`}
      style={{ fontFamily: "'IBM Plex Sans', monospace" }}
      title={`Total ${money(job.total)} − Driver ${money(job.driver_total)} − remittances = ${money(n)}`}
    >
      {money(n)}
    </span>
  );
}

function fmtDate(dt) {
  if (!dt) return "—";
  const [date, time] = dt.split("T");
  if (!date) return "—";
  const [y, m, d] = date.split("-");
  return `${m}-${d}-${y?.slice(2)}${time ? " " + time.slice(0, 5) : ""}`;
}

function getRideFlags(job) {
  const flags = [];
  if ((job.passenger_count || 1) >= 4) flags.push("4+ riders");
  const svcs = job.special_services || [];
  if (svcs.includes("child_seat")) flags.push("Child seat");
  if (svcs.includes("pet_friendly")) flags.push("Pet");
  if (svcs.includes("meet_greet")) flags.push("Meet & Greet");
  if (job.special_instructions?.trim()) flags.push("Instructions");
  return flags;
}

function lastMondayStr() {
  const d = new Date();
  const day = d.getDay(); // 0=Sun
  const offset = day === 0 ? 6 : day - 1;
  d.setDate(d.getDate() - offset);
  return d.toISOString().slice(0, 10);
}
function lastSundayStr() {
  const d = new Date(lastMondayStr());
  d.setDate(d.getDate() + 6);
  return d.toISOString().slice(0, 10);
}

function StatusBadge({ status }) {
  const map = {
    open: "bg-amber-500/15 text-amber-400 border-amber-500/30",
    partial: "bg-blue-500/15 text-blue-400 border-blue-500/30",
    paid: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  };
  return <Badge className={`${map[status] || map.open} text-[10px]`}>{status}</Badge>;
}

// ========== CHECKOUT TAB ==========
function CheckoutTab({ drivers, refresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState("7");
  const [driverFilter, setDriverFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [working, setWorking] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [fetchingFee, setFetchingFee] = useState(false);
  const [fetchFailed, setFetchFailed] = useState(false);
  const [manualFee, setManualFee] = useState("");
  const [savingFee, setSavingFee] = useState(false);

  // Close context menu on click anywhere
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    window.addEventListener("click", handler);
    return () => window.removeEventListener("click", handler);
  }, [contextMenu]);

  const markNoShow = async (job) => {
    try {
      await api.put(`/jobs/${job.id}`, { status: "no_show" });
      toast.success("Marked as no-show — set custom amounts via Review & Close");
      load(); refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update"); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days });
      if (driverFilter !== "all") params.set("driver_id", driverFilter);
      const res = await api.get(`/billing/open-jobs?${params}`);
      setJobs(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [days, driverFilter]);

  const driverMap = useMemo(() => Object.fromEntries(drivers.map((d) => [d.id, d])), [drivers]);

  const openReview = (j) => {
    setSelected(j);
    setFetchFailed(false);
    setManualFee("");
    setForm({
      base_rate: j.base_rate ?? "",
      tolls: j.tolls ?? "",
      gratuity: j.gratuity ?? "",
      driver_base_rate: j.driver_base_rate ?? j.base_rate ?? "",
      driver_gratuity: j.driver_gratuity ?? j.gratuity ?? "",
      ny_to_ny: j.ny_to_ny !== undefined ? j.ny_to_ny : true,
      is_airport_trip: !!j.is_airport_trip,
      is_congestion_zone: !!j.is_congestion_zone,
      pricing_mode: j.pricing_mode || "all_inclusive",
      all_inclusive_amount: j.all_inclusive_amount ?? "",
      pickup_location: j.pickup_location || "",
      dropoff_location: j.dropoff_location || "",
    });
  };

  const updateFields = (partial) => setForm((prev) => ({ ...prev, ...partial }));

  const needsSquareFee = selected
    && selected.payment_status === "paid"
    && selected.square_invoice_id
    && !selected.square_processing_fee;

  const handleFetchFee = async () => {
    setFetchingFee(true);
    setFetchFailed(false);
    try {
      const { data } = await api.post(`/jobs/${selected.id}/fetch-square-fee`);
      setSelected((prev) => ({ ...prev, square_processing_fee: data.square_processing_fee, square_net_received: data.square_net_received }));
      toast.success("Square fee retrieved");
    } catch (e) { console.warn("Fee fetch for checkout:", e);
      setFetchFailed(true);
      toast.error("Fee not available from Square yet.");
    } finally { setFetchingFee(false); }
  };

  const handleSaveManualFee = async () => {
    const amt = parseFloat(manualFee);
    if (!amt || amt <= 0) { toast.error("Enter a positive fee amount"); return; }
    setSavingFee(true);
    try {
      const { data } = await api.post(`/jobs/${selected.id}/square-fee`, { amount: amt });
      setSelected((prev) => ({ ...prev, square_processing_fee: data.square_processing_fee, square_net_received: data.square_net_received }));
      toast.success("Fee saved");
      setManualFee("");
      setFetchFailed(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save fee"); }
    finally { setSavingFee(false); }
  };

  const closeJob = async () => {
    setWorking(true);
    try {
      const payload = {
        base_rate: parseFloat(form.base_rate) || 0,
        tolls: parseFloat(form.tolls) || 0,
        gratuity: parseFloat(form.gratuity) || 0,
        driver_base_rate: parseFloat(form.driver_base_rate) || 0,
        driver_gratuity: parseFloat(form.driver_gratuity) || 0,
        ny_to_ny: !!form.ny_to_ny,
        is_airport_trip: !!form.is_airport_trip,
        is_congestion_zone: !!form.is_congestion_zone,
        pricing_mode: form.pricing_mode || "all_inclusive",
        all_inclusive_amount: parseFloat(form.all_inclusive_amount) || 0,
      };
      await api.post(`/jobs/${selected.id}/close`, payload);
      toast.success("Job closed");
      setSelected(null);
      load(); refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to close"); }
    finally { setWorking(false); }
  };

  return (
    <div className="space-y-4" data-testid="checkout-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-1" data-testid="checkout-days-pills">
          {["7", "14", "30"].map((d) => (
            <Button key={d} size="sm" variant={days === d ? "default" : "outline"} onClick={() => setDays(d)} className="h-8 px-3 text-xs" data-testid={`checkout-days-${d}`}>
              {d}d
            </Button>
          ))}
        </div>
        <Select value={driverFilter} onValueChange={setDriverFilter}>
          <SelectTrigger className="w-[220px] bg-card border-border h-8 text-sm" data-testid="checkout-driver-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All drivers</SelectItem>
            {drivers.map((d) => (<SelectItem key={d.id} value={d.id}>#{d.driver_number || "—"} · {d.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <span className="text-xs text-muted-foreground" data-testid="checkout-count">{jobs.length} open</span>
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead className="w-[60px]">Job#</TableHead>
              <TableHead>Driver</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Client</TableHead>
              <TableHead>Pickup</TableHead>
              <TableHead>Dropoff</TableHead>
              <TableHead className="w-8"></TableHead>
              <TableHead className="text-right">Base</TableHead>
              <TableHead className="text-right">Gratuity</TableHead>
              <TableHead className="text-right">Total</TableHead>
              {isAdmin && <TableHead className="text-right w-[80px]">Net</TableHead>}
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin ? 12 : 11} className="text-center py-8 text-muted-foreground">Loading...</TableCell></TableRow>
            ) : jobs.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 12 : 11} className="text-center py-8 text-muted-foreground">Nothing to close in this window.</TableCell></TableRow>
            ) : jobs.map((j) => {
              const dr = driverMap[j.driver_id];
              const flags = getRideFlags(j);
              return (
                <TableRow
                  key={j.id}
                  data-testid={`checkout-row-${j.id}`}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    setContextMenu({ x: e.clientX, y: e.clientY, job: j });
                  }}
                >
                  <TableCell className="text-xs font-semibold text-muted-foreground whitespace-nowrap" style={{ fontFamily: "'IBM Plex Sans', monospace" }} data-testid={`checkout-job-number-${j.id}`}>{j.job_number ? `#${j.job_number}` : "—"}</TableCell>
                  <TableCell className="text-xs font-semibold text-cyan-700 dark:text-cyan-300" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{dr ? `#${dr.driver_number}` : "—"}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{fmtDate(j.pickup_datetime)}</TableCell>
                  <TableCell className="text-xs">{j.client_name || `${j.first_name || ""} ${j.last_name || ""}`.trim() || "—"}</TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate" title={j.pickup_location}>{j.pickup_location || "—"}</TableCell>
                  <TableCell className="text-xs max-w-[160px] truncate" title={j.dropoff_location}>{j.dropoff_location || "—"}</TableCell>
                  <TableCell className="text-center">
                    {flags.length > 0 && <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400" title={flags.join(", ")} data-testid={`flag-${j.id}`} />}
                  </TableCell>
                  <TableCell className="text-right text-xs">{money(j.base_rate)}</TableCell>
                  <TableCell className="text-right text-xs">{money(j.gratuity)}</TableCell>
                  <TableCell className="text-right text-xs font-semibold">{money(j.total)}</TableCell>
                  {isAdmin && <TableCell className="text-right"><NetCell job={j} /></TableCell>}
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" onClick={() => openReview(j)} className="h-7 px-2 text-xs" data-testid={`review-close-${j.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Review & Close
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Right-click context menu — single No-Show option */}
      {contextMenu && (
        <div
          className="fixed z-50 min-w-[160px] rounded-md border border-border bg-popover shadow-lg py-1"
          style={{ top: contextMenu.y, left: contextMenu.x }}
          data-testid="checkout-context-menu"
        >
          <div className="px-3 py-1.5 text-xs text-muted-foreground border-b border-border mb-1 truncate max-w-[200px]">
            {contextMenu.job.client_name || "—"}
          </div>
          <button
            className="w-full text-left px-3 py-1.5 text-sm text-red-500 hover:bg-red-500/10 transition-colors"
            onClick={() => { markNoShow(contextMenu.job); setContextMenu(null); }}
            data-testid="checkout-context-no-show"
          >
            Mark No-Show
          </button>
        </div>
      )}

      {/* Review dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-card border-border max-w-2xl" data-testid="close-job-dialog">
          <DialogHeader>
            <DialogTitle>Review & Close Job</DialogTitle>
            {selected && (
              <DialogDescription asChild>
                <ul className="text-[8px] text-center text-muted-foreground space-y-0.5 leading-tight list-none mt-1">
                  <li>{driverMap[selected.driver_id] ? `#${driverMap[selected.driver_id].driver_number}` : "Unassigned"}</li>
                  <li>{selected.client_name || `${selected.first_name || ""} ${selected.last_name || ""}`.trim() || "—"}</li>
                  <li>{selected.pickup_location || "—"}</li>
                  <li>{selected.dropoff_location || "—"}</li>
                  <li>{fmtDate(selected.pickup_datetime)}</li>
                  {getRideFlags(selected).length > 0 && (
                    <li className="flex items-center justify-center gap-1">
                      <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-400" />
                      <span>{getRideFlags(selected).join(", ")}</span>
                    </li>
                  )}
                </ul>
              </DialogDescription>
            )}
          </DialogHeader>
          <div className="py-2">
            {selected && (
              <PricingBlock
                form={form}
                updateFields={updateFields}
                squareFee={selected?.square_processing_fee || 0}
              />
            )}
          </div>
          {/* Square fee accounting checkpoint */}
          {selected && needsSquareFee && (
            <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 space-y-2" data-testid="square-fee-block">
              <p className="text-xs text-amber-300 font-medium">
                Square processing fee is missing. House NET cannot be finalized until the Square fee is recorded.
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <Button size="sm" variant="outline" onClick={handleFetchFee} disabled={fetchingFee} className="h-7 text-xs gap-1.5 border-amber-500/40 text-amber-300 hover:bg-amber-500/10" data-testid="checkout-fetch-fee">
                  {fetchingFee ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  Fetch Sq Fee
                </Button>
              </div>
              {fetchFailed && (
                <div className="space-y-2 pt-1">
                  <p className="text-[11px] text-muted-foreground">
                    Square did not return the processing fee. Open the invoice in Square, locate the fee, and enter it manually.
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="relative w-28">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">$</span>
                      <Input type="number" min={0} step={0.01} value={manualFee} onChange={(e) => setManualFee(e.target.value)} className="bg-muted/20 border-border h-7 text-xs pl-5 pr-1" placeholder="0.00" data-testid="manual-fee-input" />
                    </div>
                    <Button size="sm" onClick={handleSaveManualFee} disabled={savingFee || !manualFee} className="h-7 text-xs" data-testid="manual-fee-save">
                      {savingFee ? "Saving..." : "Save Fee"}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setSelected(null)} data-testid="close-cancel">Cancel</Button>
            {needsSquareFee ? (
              <Button disabled className="opacity-50 cursor-not-allowed" data-testid="close-confirm">Close Job</Button>
            ) : (
              <Button onClick={closeJob} disabled={working} data-testid="close-confirm">{working ? "Closing..." : "Close Job"}</Button>
            )}
            {needsSquareFee && <p className="text-[10px] text-amber-400 w-full text-right">Record Square fee before closing this job.</p>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ========== CLOSED TAB ==========
function ClosedTab({ drivers, refresh }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [driverFilter, setDriverFilter] = useState("");
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (driverId) => {
    if (!driverId) { setJobs([]); return; }
    setLoading(true);
    try {
      const res = await api.get(`/billing/closed-jobs?driver_id=${driverId}`);
      setJobs(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const handleDriverChange = (val) => {
    setDriverFilter(val);
    load(val);
  };

  const reopenJob = async (job) => {
    try {
      await api.post(`/jobs/${job.id}/reopen`);
      toast.success("Job reopened — moved back to Checkout");
      load(driverFilter);
      refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to reopen"); }
  };

  const driverMap = useMemo(() => Object.fromEntries(drivers.map((d) => [d.id, d])), [drivers]);

  return (
    <div className="space-y-4" data-testid="closed-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={driverFilter} onValueChange={handleDriverChange}>
          <SelectTrigger className="w-[260px] bg-card border-border h-8 text-sm" data-testid="closed-driver-filter">
            <SelectValue placeholder="Select a driver..." />
          </SelectTrigger>
          <SelectContent>
            {drivers.map((d) => (<SelectItem key={d.id} value={d.id}>#{d.driver_number || "—"} · {d.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {driverFilter && <span className="text-xs text-muted-foreground" data-testid="closed-count">{jobs.length} closed, awaiting invoice</span>}
      </div>

      {!driverFilter ? (
        <div className="border border-border rounded-lg p-8 text-center text-muted-foreground text-sm" data-testid="closed-empty-prompt">
          Select a driver to view their closed jobs ready for invoicing.
        </div>
      ) : loading ? (
        <div className="border border-border rounded-lg p-8 text-center text-muted-foreground text-sm">Loading...</div>
      ) : jobs.length === 0 ? (
        <div className="border border-border rounded-lg p-8 text-center text-muted-foreground text-sm" data-testid="closed-no-jobs">
          No closed jobs pending invoice for this driver.
        </div>
      ) : (
        <div className="border border-border rounded-lg overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/20 hover:bg-muted/20">
                <TableHead className="w-[55px]">Job#</TableHead>
                <TableHead className="w-[90px]">Date</TableHead>
                <TableHead className="w-[60px]">Time</TableHead>
                <TableHead>Client</TableHead>
                <TableHead>Pickup</TableHead>
                <TableHead>Dropoff</TableHead>
                <TableHead className="text-right w-[80px]">Total</TableHead>
                {isAdmin && <TableHead className="text-right w-[80px]">Net</TableHead>}
                <TableHead className="w-[70px]">Closed</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((job) => (
                <TableRow key={job.id} className="hover:bg-muted/20" data-testid={`closed-row-${job.id}`}>
                  <TableCell className="text-xs font-semibold text-muted-foreground whitespace-nowrap" style={{ fontFamily: "'IBM Plex Sans', monospace" }} data-testid={`closed-job-number-${job.id}`}>{job.job_number ? `#${job.job_number}` : "—"}</TableCell>
                  <TableCell className="text-xs">{fmtDate(job.pickup_datetime)?.split(" ")[0] || "—"}</TableCell>
                  <TableCell className="text-xs font-medium" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>
                    {job.pickup_datetime ? job.pickup_datetime.split("T")[1]?.slice(0, 5) : "—"}
                  </TableCell>
                  <TableCell className="text-xs font-medium">{job.client_name || `${job.first_name || ""} ${job.last_name || ""}`.trim() || "—"}</TableCell>
                  <TableCell className="text-xs max-w-[180px] truncate" title={job.pickup_location}>{job.pickup_location}</TableCell>
                  <TableCell className="text-xs max-w-[180px] truncate" title={job.dropoff_location}>{job.dropoff_location}</TableCell>
                  <TableCell className="text-right text-xs font-semibold" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(job.total || job.driver_total || 0)}</TableCell>
                  {isAdmin && <TableCell className="text-right"><NetCell job={job} /></TableCell>}
                  <TableCell className="text-[10px] text-muted-foreground">{job.closed_at ? new Date(job.closed_at).toLocaleDateString() : "—"}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => reopenJob(job)} className="h-6 px-2 text-[10px] text-amber-400 hover:bg-amber-500/10" data-testid={`reopen-${job.id}`}>
                      Reopen
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

// ========== INVOICES TAB ==========
function InvoicesTab({ drivers, invoices, refresh }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showCreate, setShowCreate] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const filtered = useMemo(() => {
    let list = invoices;
    if (statusFilter !== "all") list = list.filter((i) => i.status === statusFilter);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter((i) =>
        i.invoice_number?.toLowerCase().includes(q) ||
        (i.driver_snapshot?.name || "").toLowerCase().includes(q) ||
        (i.driver_snapshot?.driver_number || "").includes(q)
      );
    }
    return list;
  }, [invoices, query, statusFilter]);

  const handleDelete = async () => {
    try {
      await api.delete(`/invoices/${deleteTarget.id}`);
      toast.success("Invoice deleted");
      setDeleteTarget(null);
      refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  return (
    <div className="space-y-4" data-testid="invoices-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder="Search invoice # or driver..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9 bg-card border-border h-8" data-testid="invoices-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px] bg-card border-border h-8 text-sm" data-testid="invoices-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="open">Open</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={() => setShowCreate(true)} data-testid="create-invoice-btn">
          <FileText className="w-4 h-4 mr-1.5" /> Create Invoice
        </Button>
      </div>

      <div className="border border-border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/20 hover:bg-muted/20">
              <TableHead>Invoice #</TableHead>
              <TableHead>Driver</TableHead>
              <TableHead>Period</TableHead>
              <TableHead className="text-right">Driver Pay</TableHead>
              <TableHead className="text-right">Net</TableHead>
              <TableHead className="text-right">Paid</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[30px]"></TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-muted-foreground">No invoices yet.</TableCell></TableRow>
            ) : filtered.map((inv) => (
              <TableRow key={inv.id} className="cursor-pointer hover:bg-muted/20" onClick={() => setOpenInvoice(inv)} data-testid={`invoice-row-${inv.id}`}>
                <TableCell className="font-semibold text-sm" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{inv.invoice_number}</TableCell>
                <TableCell className="text-sm">#{inv.driver_snapshot?.driver_number} {inv.driver_snapshot?.name}</TableCell>
                <TableCell className="text-sm">{inv.period_start} → {inv.period_end}</TableCell>
                <TableCell className="text-right text-sm">{money(inv.driver_pay_total)}</TableCell>
                <TableCell className="text-right text-sm font-semibold">{money(inv.net_payable)}</TableCell>
                <TableCell className="text-right text-sm">{money(inv.total_paid)}</TableCell>
                <TableCell className="text-right text-sm font-semibold">{money(inv.balance)}</TableCell>
                <TableCell><StatusBadge status={inv.status} /></TableCell>
                <TableCell className="w-[30px]" onClick={(e) => e.stopPropagation()}>
                  <Mail
                    className="w-3.5 h-3.5 mx-auto text-primary cursor-pointer hover:text-primary/80 transition-colors"
                    onClick={() => {
                      toast("Email invoice PDF to driver?", {
                        action: {
                          label: "Send",
                          onClick: async () => {
                            try {
                              const { data } = await api.post(`/invoices/${inv.id}/email`);
                              if (data.status === "sent") toast.success("Invoice emailed to driver");
                              else if (data.status === "skipped") toast.message(data.message || "Email skipped");
                              else toast.message("Done");
                            } catch (err) { toast.error(err.response?.data?.detail || "Failed to send"); }
                          },
                        },
                      });
                    }}
                    data-testid={`email-invoice-${inv.id}`}
                  />
                </TableCell>
                <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                  {inv.status !== "paid" && inv.total_paid === 0 ? (
                    <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(inv)} className="h-7 px-2 text-red-400 hover:bg-red-500/10" data-testid={`delete-invoice-${inv.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  ) : (
                    <Lock className="w-3.5 h-3.5 text-muted-foreground/40 inline" data-testid={`locked-${inv.id}`} />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {showCreate && <CreateInvoiceDialog drivers={drivers} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); refresh(); }} />}
      {openInvoice && <InvoiceDetailDialog invoice={openInvoice} onClose={() => setOpenInvoice(null)} onChanged={refresh} />}

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="bg-card border-border" data-testid="delete-invoice-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete invoice {deleteTarget?.invoice_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              The invoice will be removed permanently. Linked jobs will return to "closed" and can be re-invoiced.
              Base dues balance will be restored if this invoice deducted any.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500/20 text-red-400 border-red-500/40 hover:bg-red-500/30" data-testid="delete-invoice-confirm-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function CreateInvoiceDialog({ drivers, onClose, onCreated }) {
  const [driverId, setDriverId] = useState("");
  const [periodStart, setPeriodStart] = useState(lastMondayStr());
  const [periodEnd, setPeriodEnd] = useState(lastSundayStr());
  const [waiveCommission, setWaiveCommission] = useState(false);
  const [waiveFlatFee, setWaiveFlatFee] = useState(false);
  const [waiveBaseDues, setWaiveBaseDues] = useState(false);
  const [waiveNote, setWaiveNote] = useState("");
  const [invoiceNote, setInvoiceNote] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const runPreview = async () => {
    if (!driverId) return;
    setLoading(true);
    try {
      const { data } = await api.post("/invoices/preview", {
        driver_id: driverId, period_start: periodStart, period_end: periodEnd,
        waive_commission: waiveCommission, waive_flat_fee: waiveFlatFee, waive_base_dues: waiveBaseDues,
        waive_note: waiveNote, invoice_note: invoiceNote,
      });
      setPreview(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { runPreview(); }, [driverId, periodStart, periodEnd, waiveCommission, waiveFlatFee, waiveBaseDues]);

  const create = async () => {
    setSaving(true);
    try {
      await api.post("/invoices", {
        driver_id: driverId, period_start: periodStart, period_end: periodEnd,
        waive_commission: waiveCommission, waive_flat_fee: waiveFlatFee, waive_base_dues: waiveBaseDues,
        waive_note: waiveNote, invoice_note: invoiceNote,
      });
      toast.success("Invoice created");
      onCreated();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to create"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Create Invoice</DialogTitle>
          <DialogDescription>Select a driver and date range. Only closed jobs are included.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-3 py-2">
          <div className="col-span-1">
            <Label className="text-xs text-muted-foreground">Driver *</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger className="bg-muted/20 border-border" data-testid="invoice-driver-select"><SelectValue placeholder="Select driver" /></SelectTrigger>
              <SelectContent>
                {drivers.filter((d) => d.status !== "archived").map((d) => (<SelectItem key={d.id} value={d.id}>#{d.driver_number} · {d.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs text-muted-foreground">Period start</Label><Input type="date" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} data-testid="invoice-period-start" /></div>
          <div><Label className="text-xs text-muted-foreground">Period end</Label><Input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} data-testid="invoice-period-end" /></div>
        </div>

        {preview && (
          <div className="space-y-3">
            {preview.open_jobs_in_range > 0 && (
              <div className="text-xs text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-3 py-2" data-testid="open-jobs-warning">
                ⚠ {preview.open_jobs_in_range} completed job{preview.open_jobs_in_range !== 1 ? "s" : ""} in this range are not closed yet — they will be excluded.
              </div>
            )}

            <div className="border border-border rounded-md">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/10 hover:bg-muted/10">
                    <TableHead className="w-[55px]">Job#</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead className="text-right">Base</TableHead>
                    <TableHead className="text-right">Commission</TableHead>
                    <TableHead className="text-right">Driver Pay</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preview.jobs_snapshot.length === 0 ? (
                    <TableRow><TableCell colSpan={6} className="text-center py-4 text-muted-foreground text-xs">No closed jobs in range.</TableCell></TableRow>
                  ) : preview.jobs_snapshot.map((j) => (
                    <TableRow key={j.job_id}>
                      <TableCell className="text-xs font-semibold text-muted-foreground whitespace-nowrap" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{j.job_number ? `#${j.job_number}` : "—"}</TableCell>
                      <TableCell className="text-xs" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{(j.pickup_datetime || "").slice(0, 10)}</TableCell>
                      <TableCell className="text-xs">
                        {j.client_name}
                        {j.no_show && <span className="ml-1.5 text-[9px] font-bold text-red-500 tracking-wider align-middle" title="No-show">NS</span>}
                      </TableCell>
                      <TableCell className="text-right text-xs">{money(j.base_rate)}</TableCell>
                      <TableCell className="text-right text-xs">{money(j.driver_commission)}</TableCell>
                      <TableCell className="text-right text-xs">{money(j.total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            <div className="rounded-md border border-border bg-muted/5 p-3 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Driver Pay</span>
                <span className="tabular-nums font-semibold" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(preview.driver_pay_total)}</span>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Deductions</div>
              {preview.line_items.map((li, i) => {
                const label = (li.label || "").toLowerCase();
                if (label.includes("gratuity") && !li.type) return null;
                return (
                <div key={`li-${li.label}-${i}`} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-2">
                    {(li.label.includes("Commission") || li.label.includes("flat fee") || li.label.includes("dues")) && (
                      <Switch
                        checked={
                          li.label.includes("Commission") ? waiveCommission :
                          li.label.includes("flat fee") ? waiveFlatFee :
                          waiveBaseDues
                        }
                        onCheckedChange={(v) => {
                          if (li.label.includes("Commission")) setWaiveCommission(v);
                          else if (li.label.includes("flat fee")) setWaiveFlatFee(v);
                          else setWaiveBaseDues(v);
                        }}
                        data-testid={`waive-${li.label.replace(/\s+/g, '-').toLowerCase()}`}
                      />
                    )}
                    <span className={li.waived ? "line-through text-muted-foreground" : ""}>{li.label}{li.waived ? " (waived)" : ""}</span>
                  </div>
                  <span className={`tabular-nums ${li.waived ? "line-through text-muted-foreground" : ""}`} style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(-Math.abs(li.amount))}</span>
                </div>
                );
              })}
              <div className="border-t border-border pt-2 flex justify-between text-base font-semibold">
                <span>Net Payable</span>
                <span style={{ fontFamily: "'IBM Plex Sans', monospace" }} data-testid="invoice-preview-total">{money((preview.driver_pay_total || 0) - (preview.line_items || []).filter(li => !li.waived && !(li.label || "").toLowerCase().includes("gratuity")).reduce((s, li) => s + Math.abs(li.amount), 0))}</span>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3 pt-2">
          <div><Label className="text-xs text-muted-foreground">Waive note (optional)</Label><Input value={waiveNote} onChange={(e) => setWaiveNote(e.target.value)} placeholder="e.g. free week" data-testid="invoice-waive-note" /></div>
          <div><Label className="text-xs text-muted-foreground">Invoice note</Label><Input value={invoiceNote} onChange={(e) => setInvoiceNote(e.target.value)} data-testid="invoice-note" /></div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={create} disabled={saving || loading || !driverId || !preview?.jobs_snapshot?.length} data-testid="invoice-create-confirm">
            {saving ? "Creating..." : "Create Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceDetailDialog({ invoice, onClose, onChanged }) {
  const [payments, setPayments] = useState([]);
  const [inv, setInv] = useState(invoice);
  const [showPay, setShowPay] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "cash", paid_at: new Date().toISOString().slice(0, 10), note: "" });
  const [saving, setSaving] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [refundMode, setRefundMode] = useState(false);
  const [refundJob, setRefundJob] = useState(null);
  const [refundForm, setRefundForm] = useState({ amount: "", note: "" });
  const [refundSaving, setRefundSaving] = useState(false);
  const [liabilityStep, setLiabilityStep] = useState(false);
  const [liabilityForm, setLiabilityForm] = useState({ type: "none", amount: "" });

  const load = async () => {
    try {
      const [detail, pays] = await Promise.all([
        api.get(`/invoices/${invoice.id}`),
        api.get(`/invoices/${invoice.id}/payments`),
      ]);
      setInv(detail.data);
      setPayments(pays.data);
    } catch (e) { console.warn("Load invoice detail:", e); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [invoice.id]);

  const recordPayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) { toast.error("Enter a positive amount"); return; }
    setSaving(true);
    try {
      await api.post(`/invoices/${invoice.id}/payments`, {
        amount: parseFloat(payForm.amount),
        method: payForm.method, paid_at: payForm.paid_at, note: payForm.note,
      });
      toast.success("Payment recorded");
      setShowPay(false);
      setPayForm({ amount: "", method: "cash", paid_at: new Date().toISOString().slice(0, 10), note: "" });
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const printPdf = async () => {
    try {
      const res = await api.get(`/invoices/${invoice.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const w = window.open(url, "_blank");
      if (w) w.onload = () => { try { w.print(); } catch { /* ignore */ } };
    } catch { toast.error("Failed to open PDF"); }
  };

  const emailInvoice = async () => {
    setEmailing(true);
    try {
      const { data } = await api.post(`/invoices/${invoice.id}/email`);
      if (data.status === "sent") toast.success("Invoice emailed to driver");
      else if (data.status === "skipped") toast.message(`Skipped: ${data.message}`);
      else toast.message("Done");
    } catch (e) { toast.error(e.response?.data?.detail || "Email failed"); }
    finally { setEmailing(false); }
  };

  // Refund: select job
  const selectJobForRefund = (j) => {
    if (!refundMode) return;
    const alreadyRefunded = (inv.line_items || []).some((li) => li.type === "refund" && li.job_id === j.job_id);
    if (alreadyRefunded) { toast.error("Job already refunded"); return; }
    setRefundJob(j);
    setRefundForm({ amount: String(j.total || j.base_rate || 0), note: "" });
  };

  // Refund: confirm amount → move to liability step
  const confirmRefundAmount = () => {
    const amt = parseFloat(refundForm.amount);
    if (!amt || amt <= 0) { toast.error("Enter a positive amount"); return; }
    setLiabilityStep(true);
    setLiabilityForm({ type: "none", amount: "" });
  };

  // Refund: submit
  const submitRefund = async () => {
    setRefundSaving(true);
    try {
      const driverDebit = liabilityForm.type === "full"
        ? parseFloat(refundForm.amount)
        : liabilityForm.type === "partial"
          ? parseFloat(liabilityForm.amount) || 0
          : 0;
      await api.post(`/invoices/${invoice.id}/refund`, {
        job_id: refundJob.job_id,
        amount: parseFloat(refundForm.amount),
        note: refundForm.note,
        driver_liability: liabilityForm.type,
        driver_debit_amount: driverDebit,
      });
      toast.success("Refund recorded");
      setRefundJob(null);
      setLiabilityStep(false);
      setRefundMode(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Refund failed"); }
    finally { setRefundSaving(false); }
  };

  const refundedJobIds = new Set(
    (inv.line_items || []).filter((li) => li.type === "refund").map((li) => li.job_id)
  );

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card border-border max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invoice-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <span>{inv.invoice_number}</span>
            <StatusBadge status={inv.status} />
          </DialogTitle>
          <DialogDescription>
            {inv.driver_snapshot?.name} (#{inv.driver_snapshot?.driver_number}) · {inv.period_start} → {inv.period_end}
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={printPdf} data-testid="invoice-print-btn"><Printer className="w-3.5 h-3.5 mr-1.5" /> Print / PDF</Button>
          <Button size="sm" variant="outline" onClick={emailInvoice} disabled={emailing} data-testid="invoice-email-btn"><Mail className="w-3.5 h-3.5 mr-1.5" /> {emailing ? "Sending..." : "Email to Driver"}</Button>
          {inv.status !== "paid" && (
            <Button size="sm" onClick={() => setShowPay(true)} className="bg-primary/20 text-primary border border-primary/40 hover:bg-primary/30" data-testid="record-payment-btn">
              <DollarSign className="w-3.5 h-3.5 mr-1.5" /> Record Payment
            </Button>
          )}
          {!refundMode ? (
            <Button size="sm" variant="outline" onClick={() => setRefundMode(true)} className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="refund-mode-btn">
              Refund
            </Button>
          ) : (
            <Button size="sm" variant="outline" onClick={() => setRefundMode(false)} className="border-border" data-testid="refund-done-btn">
              Done
            </Button>
          )}
        </div>

        {refundMode && (
          <div className="text-xs text-red-400 bg-red-500/5 border border-red-500/20 rounded px-3 py-1.5" data-testid="refund-mode-banner">
            Select a job to refund
          </div>
        )}

        <div className="space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Jobs</div>
            <Table>
              <TableHeader><TableRow className="bg-muted/10 hover:bg-muted/10"><TableHead className="w-[55px]">Job#</TableHead><TableHead>Date</TableHead><TableHead>Client</TableHead><TableHead className="text-right">Base</TableHead><TableHead className="text-right">Commission</TableHead><TableHead className="text-right">Driver Pay</TableHead></TableRow></TableHeader>
              <TableBody>
                {(inv.jobs_snapshot || []).map((j) => {
                  const isRefunded = refundedJobIds.has(j.job_id);
                  return (
                    <TableRow
                      key={j.job_id}
                      className={`${refundMode && !isRefunded ? "cursor-pointer hover:bg-red-500/5" : ""} ${isRefunded ? "opacity-40" : ""}`}
                      onClick={() => selectJobForRefund(j)}
                      data-testid={`invoice-job-row-${j.job_id}`}
                    >
                      <TableCell className={`text-xs font-semibold text-muted-foreground whitespace-nowrap ${isRefunded ? "line-through" : ""}`} style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{j.job_number ? `#${j.job_number}` : "—"}</TableCell>
                      <TableCell className={`text-xs ${isRefunded ? "line-through" : ""}`} style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{(j.pickup_datetime || "").slice(0, 10)}</TableCell>
                      <TableCell className={`text-xs ${isRefunded ? "line-through" : ""}`}>
                        {j.client_name}
                        {j.no_show && <span className="ml-1.5 text-[9px] font-bold text-red-500 tracking-wider align-middle" title="No-show">NS</span>}
                        {isRefunded && <Badge className="ml-2 bg-red-500/15 text-red-400 border-red-500/30 text-[9px]">Refunded</Badge>}
                      </TableCell>
                      <TableCell className={`text-right text-xs ${isRefunded ? "line-through" : ""}`}>{money(j.base_rate)}</TableCell>
                      <TableCell className={`text-right text-xs ${isRefunded ? "line-through" : ""}`}>{money(j.driver_commission)}</TableCell>
                      <TableCell className={`text-right text-xs ${isRefunded ? "line-through" : ""}`}>{money(j.total)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="rounded-md border border-border bg-muted/5 p-3 space-y-1.5">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Driver Pay</span>
              <span className="tabular-nums font-semibold" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(inv.driver_pay_total)}</span>
            </div>
            {(inv.line_items || []).filter((li) => {
              const label = (li.label || "").toLowerCase();
              return !(label === "gratuity" || (label.includes("gratuity") && !li.type));
            }).length > 0 && (
              <>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground mt-2 mb-1">Deductions</div>
                {(inv.line_items || []).map((li, i) => {
                  const label = (li.label || "").toLowerCase();
                  if (label.includes("gratuity") && !li.type) return null;
                  const isRefundLine = li.type === "refund";
                  const isDebitLine = li.type === "driver_debit";
                  return (
                    <div key={`li-${li.label}-${li.type || ""}-${i}`} className={`flex justify-between text-sm ${isRefundLine ? "text-red-400" : isDebitLine ? "text-amber-400" : ""}`}>
                      <span className={li.waived ? "line-through text-muted-foreground" : ""}>{li.label}{li.waived ? " (waived)" : ""}</span>
                      <span className={`tabular-nums ${li.waived ? "line-through text-muted-foreground" : ""}`} style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(li.type === "driver_debit" ? li.amount : -Math.abs(li.amount))}</span>
                    </div>
                  );
                })}
              </>
            )}
            <div className="border-t border-border pt-2 flex justify-between text-base font-semibold">
              <span>Net Payable</span><span style={{ fontFamily: "'IBM Plex Sans', monospace" }} data-testid="invoice-net-payable">{money(inv.net_payable)}</span>
            </div>
            <div className="flex justify-between text-sm text-muted-foreground">
              <span>Paid</span><span style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{money(inv.total_paid)}</span>
            </div>
            <div className="flex justify-between text-sm font-semibold">
              <span>Balance</span><span style={{ fontFamily: "'IBM Plex Sans', monospace" }} data-testid="invoice-balance">{money(inv.balance)}</span>
            </div>
          </div>

          {payments.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Payments</div>
              <Table>
                <TableHeader><TableRow className="bg-muted/10 hover:bg-muted/10"><TableHead>Date</TableHead><TableHead>Method</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
                <TableBody>
                  {payments.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell className="text-xs" style={{ fontFamily: "'IBM Plex Sans', monospace" }}>{p.paid_at}</TableCell>
                      <TableCell className="text-xs capitalize">{p.method}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">{p.note || "—"}</TableCell>
                      <TableCell className="text-right text-xs tabular-nums">{money(p.amount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Payment dialog */}
        {showPay && (
          <Dialog open onOpenChange={(o) => !o && setShowPay(false)}>
            <DialogContent className="bg-card border-border max-w-sm" data-testid="payment-dialog">
              <DialogHeader><DialogTitle>Record Payment</DialogTitle><DialogDescription>Net payable: {money(inv.net_payable)} · Balance: {money(inv.balance)}</DialogDescription></DialogHeader>
              <div className="space-y-3 py-2">
                <div><Label className="text-xs text-muted-foreground">Amount *</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount" /></div>
                <div><Label className="text-xs text-muted-foreground">Method</Label>
                  <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                    <SelectTrigger data-testid="payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="check">Check</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label className="text-xs text-muted-foreground">Paid date</Label><Input type="date" value={payForm.paid_at} onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} data-testid="payment-date" /></div>
                <div><Label className="text-xs text-muted-foreground">Note</Label><Input value={payForm.note} onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} data-testid="payment-note" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowPay(false)}>Cancel</Button>
                <Button onClick={recordPayment} disabled={saving} data-testid="payment-save">{saving ? "Saving..." : "Record"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Refund dialog — Step 1: Amount */}
        {refundJob && !liabilityStep && (
          <Dialog open onOpenChange={(o) => { if (!o) { setRefundJob(null); } }}>
            <DialogContent className="bg-card border-border max-w-sm" data-testid="refund-dialog">
              <DialogHeader>
                <DialogTitle>Refund</DialogTitle>
                <DialogDescription>{refundJob.client_name} · {(refundJob.pickup_datetime || "").slice(0, 10)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-3 py-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Amount *</Label>
                  <Input type="number" step="0.01" value={refundForm.amount} onChange={(e) => setRefundForm({ ...refundForm, amount: e.target.value })} data-testid="refund-amount" />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Note</Label>
                  <Input value={refundForm.note} onChange={(e) => setRefundForm({ ...refundForm, note: e.target.value })} data-testid="refund-note" placeholder="Optional" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setRefundJob(null)}>Cancel</Button>
                <Button onClick={confirmRefundAmount} className="bg-red-500/20 text-red-400 border border-red-500/40 hover:bg-red-500/30" data-testid="refund-confirm">Confirm Refund</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}

        {/* Refund dialog — Step 2: Driver liability */}
        {refundJob && liabilityStep && (
          <Dialog open onOpenChange={(o) => { if (!o) { setLiabilityStep(false); setRefundJob(null); } }}>
            <DialogContent className="bg-card border-border max-w-sm" data-testid="liability-dialog">
              <DialogHeader>
                <DialogTitle>Driver Liability</DialogTitle>
                <DialogDescription>Refund {money(refundForm.amount)} — deduct from driver?</DialogDescription>
              </DialogHeader>
              <div className="space-y-2 py-2">
                {["none", "full", "partial"].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => setLiabilityForm({ ...liabilityForm, type: opt, amount: opt === "full" ? refundForm.amount : liabilityForm.amount })}
                    className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                      liabilityForm.type === opt
                        ? "bg-primary/15 text-foreground border border-primary/40"
                        : "bg-muted/10 text-muted-foreground border border-border hover:bg-muted/20"
                    }`}
                    data-testid={`liability-${opt}`}
                  >
                    {opt === "none" && "No — company absorbs"}
                    {opt === "full" && `Full — driver owes ${money(refundForm.amount)}`}
                    {opt === "partial" && "Partial — enter amount"}
                  </button>
                ))}
                {liabilityForm.type === "partial" && (
                  <div className="pt-1">
                    <Label className="text-xs text-muted-foreground">Driver debit amount</Label>
                    <Input type="number" step="0.01" value={liabilityForm.amount} onChange={(e) => setLiabilityForm({ ...liabilityForm, amount: e.target.value })} data-testid="liability-amount" />
                  </div>
                )}
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => { setLiabilityStep(false); }} data-testid="liability-back">Back</Button>
                <Button onClick={submitRefund} disabled={refundSaving} data-testid="liability-confirm">{refundSaving ? "Processing..." : "Confirm"}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ========== LEDGER TAB ==========
function LedgerTab({ drivers, initialDriverId = "" }) {
  const [driverId, setDriverId] = useState(initialDriverId);
  const [year, setYear] = useState("all");
  const [data, setData] = useState(null);
  const [zeroOpen, setZeroOpen] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const load = async () => {
    if (!driverId) { setData(null); setSummary(null); setSummaryOpen(false); return; }
    try {
      const qp = year === "all" ? "" : `?year=${year}`;
      const res = await api.get(`/drivers/${driverId}/ledger${qp}`);
      setData(res.data);
    } catch (e) { toast.error("Failed to load ledger"); }
    setSummary(null); setSummaryOpen(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [driverId, year]);

  const zeroDues = async () => {
    try {
      const { data: r } = await api.post(`/drivers/${driverId}/zero-dues`);
      toast.success(`Zeroed out ${money(r.previous_balance)} in dues`);
      setZeroOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const load1099k = async () => {
    setSummaryLoading(true);
    try {
      const { data: s } = await api.get(`/drivers/${driverId}/1099k-summary?year=${year}`);
      setSummary(s);
      setSummaryOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load summary"); }
    finally { setSummaryLoading(false); }
  };

  const exportCsv = () => {
    if (!summary) return;
    const d = summary.driver;
    const p = summary.paid_to_driver;
    const e = summary.earned_from_driver;
    const s = summary.settlement || {};
    const rows = [
      ["1099-K Year-End Summary"],
      ["Driver", `#${d.driver_number} ${d.name}`],
      ["Year", summary.year],
      [""],
      ["PAID TO DRIVER"],
      ["Driver Base Pay", p.base_pay.toFixed(2)],
      ["Tolls", p.tolls.toFixed(2)],
      ["Tips", p.tips.toFixed(2)],
      ["Gross to Driver", p.gross.toFixed(2)],
      ["Job Count", p.job_count],
      [""],
      ["EARNED FROM DRIVER"],
      ["Commission", e.commission.toFixed(2)],
      ["Flat Fees", e.flat_fees.toFixed(2)],
      ["Base Dues Deducted", e.base_dues_deducted.toFixed(2)],
      [""],
      ["SETTLEMENT"],
      ["Net Payable", (s.net_payable || 0).toFixed(2)],
      ["Total Disbursed", (s.total_disbursed || 0).toFixed(2)],
      ["Outstanding", (s.outstanding || 0).toFixed(2)],
    ];
    const esc = (v) => { const s = String(v ?? ""); return s.includes(",") || s.includes('"') ? `"${s.replace(/"/g, '""')}"` : s; };
    const csv = rows.map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `1099K_${d.driver_number}_${summary.year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const years = useMemo(() => {
    const y = new Date().getFullYear();
    return [y, y - 1, y - 2];
  }, []);

  const mono = { fontFamily: "'IBM Plex Sans', monospace" };

  return (
    <div className="space-y-4" data-testid="ledger-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={driverId} onValueChange={setDriverId}>
          <SelectTrigger className="w-[260px] bg-card border-border h-8 text-sm" data-testid="ledger-driver-select"><SelectValue placeholder="Select driver..." /></SelectTrigger>
          <SelectContent>
            {drivers.filter((d) => d.status !== "archived").map((d) => (<SelectItem key={d.id} value={d.id}>#{d.driver_number} · {d.name}</SelectItem>))}
          </SelectContent>
        </Select>
        <Select value={year} onValueChange={setYear}>
          <SelectTrigger className="w-[140px] bg-card border-border h-8 text-sm" data-testid="ledger-year-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All time</SelectItem>
            {years.map((y) => (<SelectItem key={y} value={String(y)}>{y}</SelectItem>))}
          </SelectContent>
        </Select>
        {data && (
          <Button size="sm" variant="outline" onClick={() => setZeroOpen(true)} className="h-8 text-xs" data-testid="zero-dues-btn">
            Zero Out Dues ({money(data.driver.base_dues_balance)})
          </Button>
        )}
        {data && year !== "all" && (
          <Button size="sm" variant="outline" onClick={load1099k} disabled={summaryLoading} className="h-8 text-xs" data-testid="1099k-btn">
            <FileText className="w-3.5 h-3.5 mr-1.5" />
            {summaryLoading ? "Loading..." : "1099-K"}
          </Button>
        )}
      </div>

      {!data ? (
        <div className="text-center py-16 text-muted-foreground text-sm">Select a driver to view the ledger.</div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-lg border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Net Payable</p>
              <p className="text-xl font-semibold tabular-nums" style={mono}>{money(data.totals.invoiced)}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Disbursed {year !== "all" ? `(${year})` : ""}</p>
              <p className="text-xl font-semibold text-emerald-400 tabular-nums" style={mono} data-testid="ledger-total-paid">{money(data.totals.paid)}</p>
            </div>
            <div className="rounded-lg border border-border p-3">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Outstanding</p>
              <p className="text-xl font-semibold text-amber-400 tabular-nums" style={mono}>{money(data.totals.outstanding)}</p>
            </div>
          </div>

          {summaryOpen && summary && (
            <div className="border border-border rounded-lg p-4 space-y-4" data-testid="1099k-panel">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground">1099-K Year-End Summary</p>
                  <p className="text-sm text-muted-foreground">#{summary.driver.driver_number} {summary.driver.name} · {summary.year}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={exportCsv} className="h-7 text-xs" data-testid="1099k-export-csv">
                    <Download className="w-3.5 h-3.5 mr-1.5" /> CSV
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSummaryOpen(false)} className="h-7 text-xs">Close</Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border pb-1">Paid to Driver</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Driver Base Pay</span><span className="tabular-nums" style={mono}>{money(summary.paid_to_driver.base_pay)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tolls</span><span className="tabular-nums" style={mono}>{money(summary.paid_to_driver.tolls)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Tips</span><span className="tabular-nums" style={mono}>{money(summary.paid_to_driver.tips)}</span></div>
                    <div className="flex justify-between text-sm font-semibold border-t border-border pt-1"><span>Gross to Driver</span><span className="tabular-nums" style={mono}>{money(summary.paid_to_driver.gross)}</span></div>
                    <div className="flex justify-between text-xs text-muted-foreground pt-0.5"><span>{summary.paid_to_driver.job_count} jobs</span></div>
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground border-b border-border pb-1">Earned from Driver</p>
                  <div className="space-y-1">
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Commission</span><span className="tabular-nums" style={mono}>{money(summary.earned_from_driver.commission)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Flat Fees</span><span className="tabular-nums" style={mono}>{money(summary.earned_from_driver.flat_fees)}</span></div>
                    <div className="flex justify-between text-sm"><span className="text-muted-foreground">Base Dues Deducted</span><span className="tabular-nums" style={mono}>{money(summary.earned_from_driver.base_dues_deducted)}</span></div>
                    <div className="flex justify-between text-xs text-muted-foreground pt-0.5"><span>{summary.earned_from_driver.invoice_count} invoices</span></div>
                  </div>
                </div>
              </div>

              {summary.settlement && (
                <div className="border-t border-border pt-3 space-y-1">
                  <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">Settlement</p>
                  <div className="flex justify-between text-sm font-semibold"><span>Net Payable</span><span className="tabular-nums" style={mono}>{money(summary.settlement.net_payable)}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Total Disbursed</span><span className="tabular-nums text-emerald-400" style={mono}>{money(summary.settlement.total_disbursed)}</span></div>
                  <div className="flex justify-between text-sm font-semibold"><span>Outstanding</span><span className="tabular-nums text-amber-400" style={mono}>{money(summary.settlement.outstanding)}</span></div>
                </div>
              )}
            </div>
          )}

          <div className="border border-border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader><TableRow className="bg-muted/20 hover:bg-muted/20"><TableHead>Date</TableHead><TableHead>Type</TableHead><TableHead>Invoice #</TableHead><TableHead className="text-right">Amount</TableHead><TableHead className="text-right">Running</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.events.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No activity.</TableCell></TableRow>
                ) : data.events.map((e, i) => (
                  <TableRow key={`${e.type}-${e.date}-${e.invoice_number || i}`} data-testid={`ledger-event-${i}`}>
                    <TableCell className="text-sm" style={mono}>{(e.date || "").slice(0, 10)}</TableCell>
                    <TableCell>
                      {e.type === "invoice" ? (
                        <Badge className="bg-blue-500/15 text-blue-400 border-blue-500/30 text-[10px]">Invoice</Badge>
                      ) : (
                        <Badge className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]">Payment</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm" style={mono}>{e.invoice_number || (e.invoice_id || "").slice(0, 8) || "—"}</TableCell>
                    <TableCell className={`text-right text-sm tabular-nums ${e.type === "payment" ? "text-emerald-400" : ""}`}>{e.type === "payment" ? "-" : ""}{money(e.amount)}</TableCell>
                    <TableCell className="text-right text-sm tabular-nums">{money(e.running_balance)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}

      <AlertDialog open={zeroOpen} onOpenChange={setZeroOpen}>
        <AlertDialogContent className="bg-card border-border" data-testid="zero-dues-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Zero out base dues?</AlertDialogTitle>
            <AlertDialogDescription>
              This will clear the driver's current base dues balance ({money(data?.driver?.base_dues_balance || 0)}). Logged for audit.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={zeroDues} data-testid="zero-dues-confirm-btn">Zero Out</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ========== PAGE ==========
export default function BillingPage() {
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "checkout";
  const initialDriver = searchParams.get("driver") || "";
  const [tab, setTab] = useState(initialTab);
  const [drivers, setDrivers] = useState([]);
  const [invoices, setInvoices] = useState([]);

  const loadDrivers = async () => {
    try { const res = await api.get("/drivers"); setDrivers(res.data); } catch (e) { console.warn("Load drivers:", e); }
  };
  const loadInvoices = async () => {
    try { const res = await api.get("/invoices"); setInvoices(res.data); } catch (e) { console.warn("Load invoices:", e); }
  };
  useEffect(() => { loadDrivers(); loadInvoices(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div data-testid="billing-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl tracking-tight font-light" style={{ fontFamily: "'Manrope', sans-serif" }}>Billing</h1>
          <p className="text-sm text-muted-foreground mt-1">Close jobs · Generate invoices · Record payments · Driver ledger</p>
        </div>
        <Link
          to="/admin?tab=audit&action=email"
          className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1.5 self-end"
          data-testid="billing-email-log-link"
        >
          <History className="w-3.5 h-3.5" /> Email delivery log
        </Link>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="billing-tabs">
          <TabsTrigger value="checkout" data-testid="tab-checkout">Checkout</TabsTrigger>
          <TabsTrigger value="closed" data-testid="tab-closed">Closed</TabsTrigger>
          <TabsTrigger value="invoices" data-testid="tab-invoices">Invoices</TabsTrigger>
          <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
        </TabsList>
        <TabsContent value="checkout" className="pt-4"><CheckoutTab drivers={drivers} refresh={loadInvoices} /></TabsContent>
        <TabsContent value="closed" className="pt-4"><ClosedTab drivers={drivers} refresh={loadInvoices} /></TabsContent>
        <TabsContent value="invoices" className="pt-4"><InvoicesTab drivers={drivers} invoices={invoices} refresh={loadInvoices} /></TabsContent>
        <TabsContent value="ledger" className="pt-4"><LedgerTab drivers={drivers} initialDriverId={initialDriver} /></TabsContent>
      </Tabs>
    </div>
  );
}
